import classnames from 'classnames';
import React from 'react';
import { arrayHelper } from 'utils/arrayHelper';
import { FileReaderHelper } from 'utils/FileReaderHelper';

import ButtonLink from 'reactApp/components/buttonLink';
import IconUploadFile from 'reactApp/components/icons/uploadFile';
import IconAttach from 'reactComponents/icons/attach';
import { Label } from 'reactComponents/Label/Label';

import styles from './InputFile.css';

export type InputFileChangeHandler = (event, data: InputFileValue) => void;

export type InputFileValue = {
    file?: File;
    fileContent?: string;
};

export type InputFileProps = {
    maxSizeBytes: number;
    onChange?: InputFileChangeHandler;
    file?: string | File;
    className?: string;
    errorText?: string;
    hasError?: boolean;
    withTextarea?: boolean;
    accept?: string;
    acceptedExtensions?: string | string[];
    asLink?: boolean;
    linkText?: React.ReactNode;
    canReadFile?: boolean;
};

type State = {
    value: string;
    file?: File;
};
export class InputFile extends React.PureComponent<InputFileProps, State> {
    state: State = {
        value: '',
    };

    static defaultProps = {
        canReadFile: true,
    };

    inputRef: HTMLInputElement | null = null;

    handleUploadFileChange = (event) => {
        const { files } = event.target;

        if (files && files.length) {
            this.readFile(files[0]);
        }
    };

    async readFile(file) {
        const { onChange, maxSizeBytes, canReadFile } = this.props;
        if (file.size > maxSizeBytes) {
            return '';
        }
        const value = canReadFile
            ? await FileReaderHelper.readFileAsText(file)
            : '';

        onChange && onChange(null, { file, fileContent: value });
        this.setState({
            value,
            file,
        });
    }

    handleInputChange = (event) => {
        const { onChange } = this.props;
        const { value } = event.target;

        this.setState({
            value,
        });

        onChange && onChange(event, { fileContent: value });
    };

    getAccept() {
        if (this.props.acceptedExtensions) {
            return arrayHelper
                .singleToArray(this.props.acceptedExtensions)
                .map((ext) => (ext.startsWith('.') ? ext : `.${ext}`))
                .toString();
        }

        return this.props.accept;
    }

    renderFileInput() {
        if (this.props.asLink) {
            return this.renderInputAsLink();
        }

        if (!this.props.withTextarea) {
            return (
                <div className={styles.inputDescription}>
                    <div>{this.getInputDescription()}</div>
                </div>
            );
        }
    }

    getInputDescription() {
        return this.props.withTextarea
            ? this.state.value || (this.props.file as string) || ''
            : this.state.file && this.state.file.name;
    }

    renderInputAsLink() {
        if (this.getInputDescription()) {
            return (
                <div className={styles.labelWrapper}>
                    <Label
                        onClose={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            this.removeFile();
                        }}
                        size="medium"
                    >
                        {this.getInputDescription()}
                    </Label>
                </div>
            );
        }

        return (
            <ButtonLink className={styles.buttonLink}>
                <IconAttach className={styles.iconAttach} />
                {this.props.linkText || 'Прикрепить файл'}
            </ButtonLink>
        );
    }

    removeFile = () => {
        this.setState({
            value: '',
            file: undefined,
        });
        if (this.props.onChange) {
            this.props.onChange(null, { fileContent: '' });
        }
    };

    render() {
        const { className, errorText, hasError, withTextarea } = this.props;

        const inputDescription = this.getInputDescription();

        return (
            <div className={styles.fileWrapper}>
                <div className={classnames(styles.textareaWrapper, className)}>
                    {withTextarea && (
                        <textarea
                            rows={1}
                            onChange={this.handleInputChange}
                            className={classnames(styles.textarea, className)}
                            value={inputDescription}
                        />
                    )}

                    <label>
                        <input
                            hidden
                            type={
                                this.props.asLink && this.state.file
                                    ? undefined
                                    : 'file'
                            }
                            className={styles.input}
                            onChange={this.handleUploadFileChange}
                            accept={this.getAccept()}
                        />
                        {!withTextarea && this.renderFileInput()}
                        {!this.props.asLink && (
                            <IconUploadFile className={styles.icon} />
                        )}
                    </label>
                </div>
                {hasError && (
                    <div className={styles.errorWrapper}>{errorText}</div>
                )}
            </div>
        );
    }
}
